import { useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import {
    type UseQueryResult,
    useMutation,
    useQuery,
    useQueryClient
} from 'react-query'

import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import { Icons } from 'helpers/icons'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import {
    deleteLoginSession,
    deleteLoginSessions,
    getLogginSessions
} from 'services/endpoints/settings/account/profileSettings'
import { useAuthStore } from 'store'
import { type LoginSession } from 'types/api/settings'
import { useLocalStorageClear } from 'utils'

export const LoginSessions = () => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()

    const {
        data,
        error
    }: UseQueryResult<LoginSession[], Error> = useQuery(
        'logginSessions',
        async () => await getLogginSessions(),
        {
            refetchOnWindowFocus: false
        }
    )

    const deleteSession = useMutation(
        async (device: LoginSession) => {
            await deleteLoginSession(device.id)

            return device
        },
        {
            onSuccess: (device) => {
                if (device.is_current) {
                    /*
                     * The token backing this page has just been revoked.
                     * Do not refetch authenticated queries with it.
                     */
                    useLocalStorageClear()

                    useAuthStore.getState().setAccessToken('')

                    queryClient.clear()

                    window.location.href = '/'

                    return
                }

                queryClient.invalidateQueries('logginSessions')
            },
            onError: () => {
                addToast(
                    'error',
                    t('error:errorSomethingWentWrong')
                )
            }
        }
    )

    const deleteSessions = useMutation(
        async () => await deleteLoginSessions(),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('logginSessions')
            },
            onError: () => {
                addToast(
                    'error',
                    t('error:errorSomethingWentWrong')
                )
            }
        }
    )

    useEffect(() => {
        if (error) {
            addToast(
                'error',
                t('error:errorSomethingWentWrong')
            )
        }
    }, [error, t])

    const getDeviceLabel = (device: LoginSession) => {
        if (
            device.device_name &&
            device.device_name !== 'WebKit'
        ) {
            return device.device_name
        }

        if (device.is_desktop) {
            return `${device.os || 'Desktop'} PC`
        }

        if (device.is_tablet) {
            return `${device.os || ''} Tablet`.trim()
        }

        if (device.is_mobile) {
            return `${device.os || ''} Mobile`.trim()
        }

        return device.os || 'Unknown device'
    }

    const loginSessionCard = (
        device: LoginSession
    ) => {
        const createdAt = new Date(
            device.created_at
        )

        return (
            <div
                className='settings settings__fields'
                key={device.id}
            >
                <div className='singlesetting__loginsession__card'>
                    <button
                        className='settingscard__button'
                        onClick={() =>
                            deleteSession.mutate(
                                device
                            )
                        }
                        disabled={
                            deleteSession.isLoading
                        }
                    >
                        {device.is_current
                            ? t('logOut')
                            : t('endSession')}
                    </button>

                    <div className='singlesetting__loginsession__info'>
                        <p className='singlesetting__loginsession__info__device'>
                            <img
                                src={Icons.chrome}
                                alt={t('icon')}
                            />

                            {device.browser ||
                                'Unknown browser'}

                            {device.is_current && (
                                <>
                                    {' · '}
                                    {'Current'}
                                </>
                            )}
                        </p>

                        <p className='singlesetting__loginsession__info__device'>
                            <img
                                src={Icons.android}
                                alt={t('icon')}
                            />

                            {getDeviceLabel(
                                device
                            )}

                            {device.os
                                ? ` · ${device.os}`
                                : ''}
                        </p>

                        <p className='singlesetting__loginsession__info__location'>
                            <span>
                                {device.last_used_at
                                    ? `Last active ${new Date(
                                          device.last_used_at
                                      ).toLocaleString()}`
                                    : 'Last active unknown'}
                            </span>

                            <span>
                                {createdAt.toLocaleString()}
                            </span>
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <WithHeaderSection
            headerSection={
                <LayoutHeader
                    type='basic'
                    section={t(
                        'privacySecurity'
                    )}
                    title={t(
                        'loginSessions'
                    )}
                />
            }
        >
            {Array.isArray(data) &&
                data.map((device) =>
                    loginSessionCard(
                        device
                    )
                )}

            <Button
                text={t('endAllSessions')}
                color='black'
                font='mont-15-bold'
                height='5'
                width='fit'
                padding='3'
                customClass='singlesetting__loginsession__endsessions'
                clickFn={() =>
                    deleteSessions.mutate()
                }
            />
        </WithHeaderSection>
    )
}